<template>
	<div v-if="visible" class="adlib-overlay" @click.self="handleCancel">
		<div class="adlib-modal">
			<!-- Header -->
			<div class="adlib-header">
				<h2 class="adlib-title">{{ isEdit ? "Edit Ad" : "Upload New Ad" }}</h2>
				<i class="el-icon-close adlib-close" @click="handleCancel"></i>
			</div>

			<!-- Body -->
			<div class="adlib-body">
				<!-- Drop zone -->
				<el-upload
					drag
					class="adlib-drop"
					:action="uploadUrl"
					:headers="headers"
					:accept="'.jpg,.png,.jpeg,.gif,.mp4,.webm'"
					:before-upload="beforeUpload"
					:on-success="onUploadSuccess"
					:on-error="onUploadError"
					:show-file-list="false">
					<div v-if="form.url" class="adlib-preview">
						<video v-if="form.type === 1" :src="form.url" class="adlib-thumb"></video>
						<img v-else :src="form.url" class="adlib-thumb" />
						<div class="adlib-replace">Click to replace media</div>
					</div>
					<div v-else class="adlib-drop-inner">
						<i class="el-icon-upload-success el-icon-upload2 adlib-cloud"></i>
						<div class="adlib-drop-text">Drag &amp; drop media files here or click to browse</div>
						<div class="adlib-drop-hint">Supports JPG, PNG, MP4, WebM</div>
						<button type="button" class="adlib-browse">Browse Files</button>
					</div>
				</el-upload>

				<!-- Title -->
				<div class="adlib-field">
					<label class="adlib-label">Title</label>
					<input v-model="form.name" class="adlib-input" type="text" placeholder="Ad title" maxlength="100" />
				</div>

				<!-- Company -->
				<div class="adlib-field">
					<label class="adlib-label">Company</label>
					<input v-model="form.advertiser" class="adlib-input" type="text" placeholder="Company name" maxlength="64" />
				</div>

				<!-- Placement Type -->
				<div class="adlib-field">
					<label class="adlib-label">Placement Type</label>
					<div class="adlib-placement">
						<button
							type="button"
							class="adlib-place-btn"
							:class="{ active: form.display === 1 }"
							@click="form.display = 1">
							<i class="el-icon-monitor"></i> Screen
						</button>
						<button
							type="button"
							class="adlib-place-btn"
							:class="{ active: form.display === 2 }"
							@click="form.display = 2">
							<i class="el-icon-picture-outline"></i> Banner
						</button>
					</div>
				</div>
			</div>

			<!-- Footer -->
			<div class="adlib-footer">
				<button type="button" class="adlib-btn adlib-btn-cancel" @click="handleCancel">Cancel</button>
				<button type="button" class="adlib-btn adlib-btn-upload" :disabled="loading" @click="submit">
					{{ isEdit ? "Save" : "Upload" }}
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import config from "@/http/config";

export default {
	name: "AddAdLibrary",
	props: {
		dialog: { type: Boolean, default: false },
		adLib: { type: Object, default: () => ({}) },
		isEdit: { type: Boolean, default: false },
	},
	data() {
		return {
			visible: this.dialog,
			loading: false,
			uploadUrl: config.baseUrl + this.$url.Upload.advert,
			headers: { token: this.$cookies.get("token") },
			form: this.blankForm(),
		};
	},
	watch: {
		dialog(val) {
			this.visible = val;
			if (val) this.initForm();
		},
	},
	methods: {
		blankForm() {
			return { id: "", name: "", head: "", type: 0, display: 1, url: "", advertiser: "" };
		},
		initForm() {
			if (this.isEdit && this.adLib && this.adLib.id) {
				this.form = Object.assign(this.blankForm(), this.adLib);
				if (this.form.display == null) this.form.display = 1;
			} else {
				this.form = this.blankForm();
			}
		},
		beforeUpload(file) {
			const isValid = /\.(jpg|jpeg|png|gif|mp4|webm)$/i.test(file.name);
			if (!isValid) {
				this.$message.error("Please upload a valid image or video file");
				return false;
			}
			return true;
		},
		onUploadSuccess(res) {
			if (res && res.code === 0) {
				this.form.url = res.data;
				this.form.type = /\.(mp4|webm)$/i.test(res.data) ? 1 : 0;
			} else {
				this.$message.error((res && res.msg) || "Upload failed");
			}
		},
		onUploadError() {
			this.$message.error("Upload failed");
		},
		submit() {
			if (!this.form.url) {
				this.$message.error("Please upload a media file");
				return;
			}
			this.loading = true;
			this.form.head = this.form.name;
			const editing = !!this.form.id;
			const url = editing ? this.$url.AdLibrary.update : this.$url.AdLibrary.insert;
			const method = editing ? this.$method.put : this.$method.post;
			this.$api
				.request(url, method, this.form)
				.then(res => {
					this.loading = false;
					if (res.code === 0) {
						this.$notify({ title: this.$t("common.tip"), message: res.msg, type: "success" });
						this.handleCancel(true);
					} else {
						this.$message.error(res.msg);
					}
				})
				.catch(err => {
					this.loading = false;
					this.$message.error(err && err.msg ? err.msg : "Save failed");
				});
		},
		handleCancel(refresh) {
			this.visible = false;
			this.$emit("cancel", false, refresh === true);
		},
	},
};
</script>

<style scoped>
.adlib-overlay {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 3000;
}
.adlib-modal {
	width: 560px;
	max-width: 92vw;
	max-height: 90vh;
	overflow-y: auto;
	background: var(--vtm-surface);
	border-radius: 12px;
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
}
.adlib-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 20px 24px;
	border-bottom: 1px solid var(--vtm-line);
}
.adlib-title {
	margin: 0;
	font-size: 22px;
	font-weight: 700;
	color: var(--vtm-heading);
}
.adlib-close {
	font-size: 22px;
	color: var(--vtm-ink-3);
	cursor: pointer;
}
.adlib-close:hover {
	color: var(--vtm-heading);
}
.adlib-body {
	padding: 20px 24px 4px;
}
/* Drop zone */
.adlib-drop >>> .el-upload,
.adlib-drop >>> .el-upload-dragger {
	width: 100%;
}
.adlib-drop >>> .el-upload-dragger {
	height: 200px;
	border: 2px dashed var(--vtm-line-2);
	border-radius: 10px;
	background: var(--vtm-surface-2);
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
}
.adlib-drop >>> .el-upload-dragger:hover {
	border-color: var(--vtm-mint-dim);
}
.adlib-drop-inner {
	display: flex;
	flex-direction: column;
	align-items: center;
}
.adlib-cloud {
	font-size: 40px;
	color: var(--vtm-ink);
}
.adlib-drop-text {
	color: var(--vtm-ink);
	font-size: 17px;
	font-weight: 700;
	margin-top: 14px;
}
.adlib-drop-hint {
	color: var(--vtm-ink-3);
	font-size: 13px;
	margin-top: 8px;
}
.adlib-browse {
	margin-top: 16px;
	padding: 9px 20px;
	background: var(--vtm-well);
	border: 1px solid var(--vtm-line-2);
	border-radius: 8px;
	font-size: 14px;
	font-weight: 600;
	color: var(--vtm-ink);
	cursor: pointer;
}
.adlib-browse:hover {
	background: var(--vtm-line);
}
.adlib-preview {
	display: flex;
	flex-direction: column;
	align-items: center;
}
.adlib-thumb {
	max-height: 160px;
	max-width: 90%;
	object-fit: contain;
	border-radius: 8px;
}
.adlib-replace {
	color: var(--vtm-mint-ink);
	font-size: 13px;
	margin-top: 10px;
}
/* Fields */
.adlib-field {
	margin-top: 20px;
}
.adlib-label {
	display: block;
	font-size: 16px;
	font-weight: 700;
	color: var(--vtm-ink);
	margin-bottom: 8px;
}
.adlib-input {
	width: 100%;
	box-sizing: border-box;
	padding: 11px 14px;
	border: 1px solid var(--vtm-line-2);
	background: var(--vtm-surface);
	border-radius: 8px;
	font-size: 15px;
	color: var(--vtm-ink);
	outline: none;
}
.adlib-input::placeholder {
	color: var(--vtm-ink-3);
}
.adlib-input:focus {
	border-color: var(--vtm-mint-dim);
}
/* Placement toggle */
.adlib-placement {
	display: flex;
	gap: 14px;
}
.adlib-place-btn {
	flex: 1;
	padding: 13px 0;
	border: 1px solid var(--vtm-line-2);
	border-radius: 8px;
	background: var(--vtm-surface);
	font-size: 15px;
	font-weight: 600;
	color: var(--vtm-ink);
	cursor: pointer;
	transition: all 0.15s;
}
.adlib-place-btn i {
	margin-right: 6px;
}
.adlib-place-btn.active {
	border-color: var(--vtm-mint-dim);
	background: var(--vtm-mint-soft);
	color: var(--vtm-mint-ink);
}
/* Footer */
.adlib-footer {
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	padding: 16px 24px;
	border-top: 1px solid var(--vtm-line);
	background: var(--vtm-surface-2);
	border-radius: 0 0 12px 12px;
	margin-top: 20px;
}
.adlib-btn {
	padding: 11px 26px;
	border-radius: 8px;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;
}
.adlib-btn-cancel {
	background: var(--vtm-surface);
	border: 1px solid var(--vtm-line-2);
	color: var(--vtm-ink);
}
.adlib-btn-cancel:hover {
	background: var(--vtm-well);
}
.adlib-btn-upload {
	background: var(--vtm-mint);
	border: 1px solid var(--vtm-mint);
	color: var(--vtm-navy);
}
.adlib-btn-upload:hover {
	background: var(--vtm-mint-dim);
}
.adlib-btn-upload:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}
</style>
